import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePath } from "@/src/contexts/path/path";
import css from "./.module.scss";
import { Select } from "@aqar-app/react-ui";
import { Icon } from "@/components/icon";
import dayjs from "dayjs";
import Link from "next/link";
import { DEFAULT_CATEGORY } from "@/src/lib/path-resolver-middlewares/category";
import { trackExploreAqarPlus } from "@/lib/tracking/trackAqarPlus";

const BOOLEAN_FILTERS = [
  { key: "all", label: { ar: "الكل" } },
  { key: "unverified_owner", label: { ar: "مالك" } },
  { key: "verified_owner", label: { ar: "مالك مؤكد" } },
  { key: "direct_from_owners", label: { ar: "مباشر المالك" } },
  { key: "unique_listing", label: { ar: "إعلانات فريدة" } },
];
const RESET_ALL_BOOLEAN = {
  unverified_owner: undefined,
  verified_owner: undefined,
  direct_from_owners: undefined,
  unique_listing: undefined,
};
const ONE_DAY = 24 * 60 * 60 * 1000;

export function AqarPlusFilters() {
  const buttons = useRef();
  const {
    listing,
    updateContext,
    category = DEFAULT_CATEGORY,
    filters,
  } = usePath();
  const radioFilter = useMemo(
    () => BOOLEAN_FILTERS.find((f) => filters[f.key]?.eq)?.key || "all",
    [filters],
  );
  const [crateOptions] = useState([
    { label: "الكل" },
    { label: "اليوم", value: dayjs().startOf("d").unix() },
    { label: "3 أيام", value: dayjs().startOf("d").subtract(3, "d").unix() },
    { label: "أسبوع", value: dayjs().startOf("d").subtract(1, "w").unix() },
    { label: "أسبوعان", value: dayjs().startOf("d").subtract(2, "w").unix() },
    { label: "شهر", value: dayjs().startOf("d").subtract(1, "m").unix() },
  ]);

  useEffect(() => {
    const scrollIndex = BOOLEAN_FILTERS.findIndex((f) => f.key === radioFilter);
    if (scrollIndex === -1) return;
    buttons.current?.children[scrollIndex]?.scrollIntoView({
      inline: "center",
    });
  }, [radioFilter]);

  return (
    <div className={css.plusFilters}>
      <p className={css.title}>فلاتر عقار +</p>
      <p>عرض الإعلانات من</p>
      <div ref={buttons} className={css.horizontalButtons}>
        {BOOLEAN_FILTERS.map(({ key, label }) => (
          <button
            onClick={() => {
              const filters_ = {
                ...filters,
                ...RESET_ALL_BOOLEAN,
                [key]: { eq: 1 },
                all: undefined,
              };
              if (!category)
                return updateContext({
                  category: DEFAULT_CATEGORY,
                  filters: filters_,
                });

              if (listing)
                return updateContext({
                  filters: filters_,
                  listing: undefined,
                  city: undefined,
                  direction: undefined,
                  district: undefined,
                });

              updateContext({
                filters: filters_,
              });
            }}
            className={radioFilter === key ? css.active : undefined}
            key={key}
            type="button"
          >
            {label.ar}
          </button>
        ))}
      </div>

      <p>البحث بالنص</p>
      <div className={css.input}>
        <input
          placeholder="سطح، مدخل خاص، مودرن..."
          value={filters.keyword}
          onChange={(e) => {
            if (!e.target.value)
              return updateContext({
                category: category || DEFAULT_CATEGORY,
                filters: {
                  ...filters,
                  keyword: undefined,
                },
              });

            if (listing)
              return updateContext({
                category: category || DEFAULT_CATEGORY,
                filters: {
                  ...filters,
                  keyword: e.target.value,
                },
                listing: undefined,
                city: undefined,
                direction: undefined,
                district: undefined,
              });
            updateContext({
              category: category || DEFAULT_CATEGORY,
              filters: {
                ...filters,
                keyword: e.target.value,
              },
            });
          }}
        />
        <Icon className={css.icon} name="Search" width={15} height={15} />
      </div>

      <p>تاريخ نشر الإعلان</p>
      <div className={css.padding}>
        <Select
          value={filters?.create_time?.gte}
          onSelect={(v) => {
            if (listing)
              return updateContext({
                category: category || DEFAULT_CATEGORY,
                filters: {
                  ...filters,
                  create_time: v ? { gte: v } : undefined,
                },
                listing: undefined,
                city: undefined,
                direction: undefined,
                district: undefined,
              });
            updateContext({
              category: category || DEFAULT_CATEGORY,
              filters: {
                ...filters,
                create_time: v ? { gte: v } : undefined,
              },
            });
          }}
        >
          {crateOptions.map((opt) => (
            <Select.Option value={opt.value} key={opt.value || "all"}>
              <button type="button">{opt.label}</button>
            </Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );
}

export function AqarPlusNotSubscribed() {
  const { previousPath } = usePath();

  return (
    <div className={css.plusFilters}>
      <p className={css.title}>فلاتر عقار +</p>
      <Link
        href="/aqar-plus"
        onClick={() => {
          trackExploreAqarPlus({
            journey: "search_filters",
            source: previousPath || decodeURI(document.referrer),
          });
        }}
      >
        اكتشف مزايا عقار +
      </Link>
    </div>
  );
}
